import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useUIStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { trackEvent } from '@/constants/utils/analytics';
import { BORDER_RADIUS, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';
import useWorkModeIntentSuggestions from '@/hooks/useWorkModeIntentSuggestions';
import { isDesktopEditorSidebarType } from '@/constants/utils/files';

const createStyles = (colors, isXS, messageWidth, variant) => StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: variant === 'conversation' ? (isXS ? 15 : 20) : 12,
    paddingVertical: 6,
    marginBottom: variant === 'conversation' ? 8 : 0,
    maxWidth: variant === 'conversation'
      ? (messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%')
      : '100%',
    marginLeft: variant === 'conversation' ? 'auto' : undefined,
    marginRight: variant === 'conversation' ? 'auto' : undefined,
    flexShrink: 0,
    position: 'relative',
    zIndex: variant === 'conversation' ? 12 : 1,
    ...(Platform.OS === 'web' && variant === 'conversation' ? { boxSizing: 'border-box' } : {}),
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: BORDER_RADIUS.round,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: variant === 'conversation' ? colors.solid_color_block : colors.solid_color_tertiary,
    maxWidth: '100%',
    flexShrink: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      },
    }),
  },
  chipHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_input,
  },
  chipLabel: {
    flexShrink: 1,
  },
});

const WorkModeIntentSuggestions = ({ contextId, variant = 'home', onActivate }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const messageWidth = useUIStore(state => state.messageWidth);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const styles = useMemo(
    () => createStyles(colors, isXS, messageWidth, variant),
    [colors, isXS, messageWidth, variant]
  );

  const { shouldSuggest } = useWorkModeIntentSuggestions(contextId);

  const handlePress = useCallback(() => {
    trackEvent('work', 'intent_suggestion_clicked', variant);
    onActivate?.();
  }, [onActivate, variant]);

  if (isContextSidebarVisible && isDesktopEditorSidebarType(contextSidebarType)) return null;
  if (!shouldSuggest) return null;

  return (
    <View style={styles.container} accessibilityRole="list">
      <Pressable
        style={({ hovered, pressed }) => [
          styles.chip,
          hovered && styles.chipHovered,
          pressed && { opacity: 0.8 },
        ]}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={t('home.tryWorkMode')}
      >
        <Icon name="briefcase" size={12} color={colors.text_color_secondary} />
        <Text size="s" color="secondary" weight="semibold" numberOfLines={1} style={styles.chipLabel}>
          {t('home.tryWorkMode')}
        </Text>
        <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
      </Pressable>
    </View>
  );
};

export default React.memo(WorkModeIntentSuggestions);
